import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Paper,
  Switch,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import _ from 'lodash';
import superadminServices from 'app/services/superadmin-services';
import { ResponsiveDataTable, StandardFiltersBar, StandardPageHeader, StandardSearchField } from 'app/shared/ui';

const CompanyPluginsRootIndex = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return records;

    const q = searchQuery.trim().toLowerCase();
    return _.filter(records, (record) => (`${record?.name || ''} ${record?.slug || ''} ${record?.category || ''}`).toLowerCase().includes(q));
  }, [records, searchQuery]);

  const loadCompanies = () => {
    superadminServices.getCompanies('?page=1&include_archived=false')
      .then((resp) => {
        const items = resp?.data || [];
        setCompanies(items);
        if (!selectedCompanyId && items[0]?.id) {
          setSelectedCompanyId(items[0].id);
        }
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar empresas.' }));
  };

  const loadRecords = (companyId) => {
    if (!companyId) {
      setRecords([]);
      return;
    }

    setLoading(true);
    superadminServices.getCompanyPluginAvailabilities(companyId)
      .then((resp) => {
        setRecords(resp || []);
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar disponibilidad de plugins.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadRecords(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const toggleAvailability = (record) => {
    superadminServices.toggleCompanyPluginAvailability(selectedCompanyId, record.id)
      .then((resp) => {
        setRecords((prev) => prev.map((item) => (
          item.id === record.id
            ? { ...item, root_available: resp?.root_available, installed: resp?.root_available ? item.installed : false, installed_enabled: resp?.root_available ? item.installed_enabled : false }
            : item
        )));
        setNotice({ severity: 'success', text: 'Disponibilidad raíz actualizada.' });
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo actualizar disponibilidad raíz.';
        setNotice({ severity: 'error', text: msg });
      });
  };

  const selectedCompany = _.find(companies, (company) => `${company.id}` === `${selectedCompanyId}`) || null;

  const renderAvailabilitySwitch = (record) => (
    <Switch
      checked={!!record.root_available}
      onChange={() => toggleAvailability(record)}
    />
  );

  const columns = [
    {
      key: 'plugin',
      label: 'Plugin',
      render: (record) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{record.name}</Typography>
          <Typography variant="caption" color="text.secondary">{record.slug}</Typography>
        </Box>
      ),
    },
    {
      key: 'category',
      label: 'Categoría',
      render: (record) => record.category || '-',
    },
    {
      key: 'company_status',
      label: 'Estado empresa',
      render: (record) => (
        record.root_available
          ? <Chip size="small" color="success" label="Disponible" />
          : <Chip size="small" color="warning" label="No disponible" />
      ),
    },
    {
      key: 'installation',
      label: 'Instalación',
      render: (record) => (
        record.installed
          ? (record.installed_enabled ? <Chip size="small" color="success" label="Activo" /> : <Chip size="small" color="default" label="Instalado" />)
          : <Chip size="small" color="default" label="No instalado" />
      ),
    },
    {
      key: 'root_toggle',
      label: 'Habilitar raíz',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderAvailabilitySwitch(record),
    },
  ];

  return (
    <Box>
      <StandardPageHeader title="Superadministración · Plugins por empresa" />

      <StandardFiltersBar>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 1.25 }}>
          <Autocomplete
            size="small"
            options={companies}
            getOptionLabel={(option) => option?.name || ''}
            value={selectedCompany}
            onChange={(e, value) => setSelectedCompanyId(value?.id || '')}
            renderInput={(params) => <TextField {...params} label="Empresa" />}
          />
          <StandardSearchField
            fullWidth
            placeholder="Buscar plugin"
            onChange={(e) => setSearchQuery(e.target.value || '')}
          />
        </Box>
      </StandardFiltersBar>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            rows={filteredRecords}
            rowKey="id"
            emptyMessage="No hay plugins para mostrar."
            mobileTitle={(record) => record?.name || '-'}
            mobileSubtitle={(record) => record?.slug || '-'}
            renderRowActions={(record) => (
              <Stack direction="row" alignItems="center">
                {renderAvailabilitySwitch(record)}
              </Stack>
            )}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CompanyPluginsRootIndex;
